import { useEffect, useState } from 'react';
import {
  extractPaymentInstructions,
  extractPaymentRedirectUrl,
  type WalletPaymentResponse,
} from '@/domain/payments';
import type { PaymentSuccessMode } from '@/domain/payment-providers';
import { cn } from '@/lib/cn';
import { Button, Modal, Toast } from '@/ui/common';

export interface PaymentResultProps {
  response: WalletPaymentResponse | null;
  successMode: PaymentSuccessMode;
  successTitle?: string;
  errorTitle?: string;
  closeLabel?: string;
  copyLabel?: string;
  copiedLabel?: string;
  className?: string;
  onDismiss?: () => void;
}

export function PaymentResult({
  response,
  successMode,
  successTitle = 'Payment submitted',
  errorTitle = 'Payment failed',
  closeLabel = 'Close',
  copyLabel = 'Copy',
  copiedLabel = 'Copied',
  className,
  onDismiss,
}: PaymentResultProps) {
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!response?.Success) return;

    const redirectUrl = extractPaymentRedirectUrl(response);

    if (successMode === 'redirect' && redirectUrl) {
      window.location.assign(redirectUrl);
      return;
    }

    if (successMode === 'popup' && redirectUrl) {
      window.open(redirectUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (successMode === 'iframe' && redirectUrl) {
      setIframeUrl(redirectUrl);
    }
  }, [response, successMode]);

  if (!response) return null;

  if (!response.Success) {
    return (
      <Toast tone="danger" className={className}>
        {`${errorTitle}: ${response.Error?.trim() || 'Please try again.'}`}
      </Toast>
    );
  }

  const instructions = extractPaymentInstructions(response);
  const showInline =
    successMode === 'inline' ||
    (successMode === 'notify' && instructions.length > 0) ||
    (successMode === 'iframe' && !iframeUrl && instructions.length > 0);

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <Toast tone="success">
        {successMode === 'notify' && instructions.length === 0
          ? `${successTitle}. Your request was submitted successfully.`
          : successTitle}
      </Toast>

      {showInline && instructions.length > 0 ? (
        <div className="flex flex-col gap-2 rounded-xl border border-border-subtle bg-bg-surface px-4 py-3">
          {instructions.map((row) => (
            <div key={row.label} className="flex items-start justify-between gap-3 text-[13px]">
              <div className="min-w-0">
                <p className="text-text-secondary">{row.label}</p>
                <p className="break-all font-medium text-text-primary">{row.value}</p>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(row.value);
                    setCopiedKey(row.label);
                    window.setTimeout(() => setCopiedKey(null), 1500);
                  } catch {
                    // Clipboard may be unavailable.
                  }
                }}
              >
                {copiedKey === row.label ? copiedLabel : copyLabel}
              </Button>
            </div>
          ))}
        </div>
      ) : null}

      <Modal
        open={Boolean(iframeUrl)}
        onClose={() => {
          setIframeUrl(null);
          onDismiss?.();
        }}
        aria-label={successTitle}
        panelClassName="h-[80vh] w-[min(960px,94vw)] overflow-hidden p-0"
      >
        {iframeUrl ? (
          <iframe title={successTitle} src={iframeUrl} className="h-full w-full border-0" />
        ) : null}
        <div className="absolute right-3 bottom-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setIframeUrl(null);
              onDismiss?.();
            }}
          >
            {closeLabel}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
